/** Structured reject-video reasons — used by HireDetail.tsx's ReviewVideoDialog
 *  (admin's own reject dialog). Mirrors Brand-Dashboard/src/lib/rejectReasons.ts
 *  and Brand-Dashboard/worker/src/videoReview.ts's REJECT_REASON_TAGS — keep
 *  all three in sync. Schema: creator-app/supabase/migrations/
 *  20260923000006_cr_video_submissions_reject_reason_tags.sql
 *
 *  One shared list for both job types. 'concept_mismatch' only makes sense on
 *  a viral-remake job (a regular video has no reference to drift from), so the
 *  dialog filters it out via rejectReasonTagsFor(isViralRemake) rather than the
 *  DB enforcing it — every other tag applies identically to both. Unlike a
 *  brand rejection, admin writes this column directly (no worker gate), so
 *  this file is the only enforcement on the admin side — keep the option list
 *  and the min-length rules below exactly in sync with the worker's copy. */
export type RejectReasonTag =
  | 'concept_mismatch'
  | 'product_wrong_or_missing'
  | 'mispronounced'
  | 'wrong_claims'
  | 'wrong_price'
  | 'policy_violation'
  | 'unusable_quality'
  | 'wrong_product_tagged'
  | 'other'

export interface RejectReasonOption {
  key: RejectReasonTag
  label: string
  /** Only offered on a viral-remake job. */
  remakeOnly?: boolean
}

export const REJECT_REASON_OPTIONS: ReadonlyArray<RejectReasonOption> = [
  { key: 'concept_mismatch', remakeOnly: true,
    label: "Doesn't follow the original concept (hook, format, or key moments missing or changed)" },
  { key: 'product_wrong_or_missing',
    label: 'Wrong product shown, or your product is missing or barely on screen' },
  { key: 'mispronounced',
    label: 'Brand or product name badly mispronounced' },
  { key: 'wrong_claims',
    label: 'Wrong, missing, or made-up ingredients, features, or claims' },
  { key: 'wrong_price',
    label: 'Wrong price, offer, or discount code' },
  { key: 'policy_violation',
    label: 'Claims that break TikTok Shop rules (medical claims, "cures," guaranteed results)' },
  { key: 'unusable_quality',
    label: 'Unusable quality: bad audio, blurry footage, product cut off' },
  { key: 'wrong_product_tagged',
    label: 'Wrong product tagged' },
  { key: 'other',
    label: 'Other (please explain)' },
]

/** The options to offer for a given job type — everything except
 *  concept_mismatch, unless this is a remake. */
export function rejectReasonTagsFor(isViralRemake: boolean): RejectReasonOption[] {
  return REJECT_REASON_OPTIONS.filter(o => !o.remakeOnly || isViralRemake)
}

/** Short form of each REJECT_REASON_OPTIONS label, for rendering as a compact
 *  pill (e.g. next to a rejected video) rather than the full sentence the
 *  dialog's checkbox list shows. */
const REJECT_REASON_SHORT_LABELS: Record<RejectReasonTag, string> = {
  concept_mismatch: 'Concept mismatch',
  product_wrong_or_missing: 'Product missing/wrong',
  mispronounced: 'Mispronounced',
  wrong_claims: 'Wrong claims',
  wrong_price: 'Wrong price/offer',
  policy_violation: 'Policy violation',
  unusable_quality: 'Unusable quality',
  wrong_product_tagged: 'Wrong product tagged',
  other: 'Other',
}

/** Turns a rejected video's raw reject_reason_tags into display labels —
 *  unknown/malformed entries are dropped rather than shown raw. Returns [] for
 *  null/undefined/empty, same as an untagged (pre-2026-09-23) rejection. */
export function rejectReasonLabels(tags: string[] | null | undefined): string[] {
  if (!tags?.length) return []
  return tags
    .filter((t): t is RejectReasonTag => t in REJECT_REASON_SHORT_LABELS)
    .map(t => REJECT_REASON_SHORT_LABELS[t])
}

/** Mirrors the worker's MIN_REJECT_REASON / MAX_REJECT_REASON. Fixed
 *  regardless of which tags are picked — see Brand-Dashboard's copy of this
 *  file for why the 'other' floor moved to MIN_REJECT_OTHER_NOTE below
 *  instead of bumping this one. */
export const MIN_REJECT_NOTE = 30

/** The floor/ceiling for the SEPARATE "what does 'other' mean?" box that
 *  appears once the 'other' tag is checked — never shown to the creator, a
 *  queryable companion to reject_reason_tags. */
export const MIN_REJECT_OTHER_NOTE = 75
export const MAX_REJECT_OTHER_NOTE = 500

/** The "why these aren't valid" copy, and the note placeholder — both differ
 *  between a regular video (the brief is a suggestion) and a remake (the brief
 *  IS the job, so drifting from it is a real complaint). */
export function rejectBriefReminder(isViralRemake: boolean): string {
  return isViralRemake
    ? "Remakes should follow the original's hook, format, and key moments, but in the creator's own voice and setting. It won't be a word-for-word or shot-for-shot copy."
    : "For regular videos, the brief is a suggestion. Creators are free to use their own style, script, and format, so skipping brief ideas isn't a reason to reject."
}

export function rejectNotValidReasons(isViralRemake: boolean): string {
  return isViralRemake
    ? "The style wasn't liked, the video didn't get views or sales, the creator used their own words or setting while keeping the concept, or you want changes that weren't asked for up front."
    : "The style wasn't liked, the video didn't get views or sales, the creator took a different creative approach than the brief suggested, or you want changes that weren't asked for up front."
}

export function rejectNotePlaceholder(isViralRemake: boolean): string {
  return isViralRemake
    ? 'Be specific so the creator can fix it fast. Example: The original opens with the product being dropped into water. This version skips that hook. Please add it back in the first 3 seconds.'
    : "Be specific so the creator can fix it fast. Example: At 0:12 they say the cream has retinol, but it doesn't. Ask them to re-record that line."
}

/** Placeholder for the separate "other" box — appears once that tag is
 *  checked, asking what the fixed list didn't cover. Never shown to the
 *  creator, so this asks for BMC's benefit, not theirs. */
export function otherReasonPlaceholder(isViralRemake: boolean): string {
  return isViralRemake
    ? 'Explain the issue and how it goes against the original concept or misrepresents the product.'
    : 'Explain the issue and how it misrepresents the product.'
}
